import React from "react";
import DatePickFormField from "~/visuals/organisms/DatePickFormField";
import { ChargeFieldState } from "./types";
import { EmployeePicker } from "~/visuals/organisms/EmployeePickerFormField";
import { EeCodePickerFormField } from "~/visuals/organisms/EeCodePickerFormField";
import TextFormField from "~/components/text-form-field";
import ProjectPickerFormField from "~/visuals/organisms/ProjectPickerFormField";
import moment, { Moment } from "moment";
import CurrencyFormField from "~/visuals/organisms/CurrencyFormField";
import EnumDropdownFormField from "~/visuals/organisms/EnumDropdownFormField";
import { ChargeType, IExpenseRate, TaskRate } from "~/gql/types";
import { RateUnitLabels } from "~/gql/types2";
import ExpensePickerFormField from "~/visuals/organisms/ExpensePickerFormField";
import { Body1 } from "@material/react-typography";
import styled from "@emotion/styled";
import TaskPickerFormField from "~/visuals/organisms/TaskPickerFormField";
import { validateProjectDate } from "./functions/validateProjectDate";

const FieldWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  .mdc-text-field-helper-line {
    display: none;
  }
`;

const Message = styled(Body1)`
  margin: 0;
  font-size: 16px;
  padding: 0 16px;
`;

export const ChargeDatePicker: React.FC<ChargeFieldState> = (props) => {
  return (
    <DatePickFormField
      {...{
        disabled: props.disabled,
        required: props.required,
        formField: "date",
        label: "Date",
        helperText: "",
      }}
    />
  );
};

export const ChargeEmployeePicker: React.FC<ChargeFieldState> = (props) => {
  return (
    <EmployeePicker
      {...{
        disabled: props.disabled,
        required: props.required,
        formField: "employee",
        label: "Employee",
        helperText: "",
      }}
    />
  );
};

{
  /* factor into resolver on backend rather than querying 40,000 ee code records */
}
export const ChargeEeCodePicker: React.FC<ChargeFieldState> = (props) => {
  return (
    <EeCodePickerFormField
      {...{
        disabled: props.disabled,
        required: props.required,
        formField: "eeCode",
        label: "EE code",
        helperText: "",
      }}
    />
  );
};

export const ChargeInvoiceNotes: React.FC<ChargeFieldState> = (props) => {
  return (
    <TextFormField
      {...{
        disabled: props.disabled,
        required: true,
        label: "Invoice notes",
        helperText: "",
        formField: "notes",
        textarea: true,
      }}
    />
  );
};

export const ChargeEditNotes: React.FC<ChargeFieldState> = (props) => {
  return (
    <TextFormField
      {...{
        disabled: props.disabled,
        required: true,
        label: "Admin notes",
        helperText: "",
        formField: "editNotes",
        textarea: true,
      }}
    />
  );
};

export const ChargeQuantityField: React.FC<ChargeFieldState> = (props) => {
  return (
    <TextFormField
      {...{
        disabled: props.disabled,
        required: props.required,
        label: "Quantity",
        helperText: "",
        formField: "quantity",
        type: "number",
      }}
    />
  );
};

type ChargeProjectFieldProps = ChargeFieldState & {
  date: Moment | null;
};

export const ChargeProjectField: React.FC<ChargeProjectFieldProps> = (
  props
) => {
  // check that values are being passed into validator
  return (
    <ProjectPickerFormField
      {...{
        disabled: props.disabled,
        required: props.required,
        formField: "project",
        label: "Project",
        helperText: "",
        date: (props.date ?? moment()).format("YYYY-MM-DD"),
        onChange: validateProjectDate,
      }}
    />
  );
};

type ChargeRateAmountFieldProps = ChargeFieldState & {
  rateAmountValidationMessage: string | null;
};

export const ChargeRateAmountField: React.FC<ChargeRateAmountFieldProps> = (
  props
) => {
  return (
    <FieldWrapper>
      <CurrencyFormField
        {...{
          disabled: props.disabled,
          required: props.required,
          label: "Rate Amount",
          helperText: "",
          formField: "rateAmount",
        }}
      />
      {props.rateAmountValidationMessage && (
        <Message>{props.rateAmountValidationMessage}</Message>
      )}
    </FieldWrapper>
  );
};

export const ChargeRateUnitField: React.FC<ChargeFieldState> = (props) => {
  return (
    <EnumDropdownFormField
      {...{
        disabled: props.disabled,
        required: props.required,
        label: "Rate Unit",
        formField: "unit",
        helperText: "Select the rate unit type",
        options: RateUnitLabels,
      }}
    />
  );
};

type ChargeRateFieldProps = {
  rate: ChargeFieldState;
  rateSuggestions: IExpenseRate[] | TaskRate[] | null;
  type: ChargeType;
};

export const ChargeRateField: React.FC<ChargeRateFieldProps> = ({
  rate,
  rateSuggestions,
  type,
}) => {
  return (
    <FieldWrapper>
      {type === ChargeType.Expense && (
        <ExpensePickerFormField
          formField="rate"
          label="Rate"
          helperText={""}
          // fix type casting
          expenseRates={(rateSuggestions ?? []) as IExpenseRate[]}
          showInvoiceLabel={true}
          disabled={rate.disabled}
          required={rate.required}
        />
      )}
      {type === ChargeType.Task && (
        <TaskPickerFormField
          formField="rate"
          label="Rate"
          helperText={""}
          // fix type casting
          taskRates={(rateSuggestions ?? []) as TaskRate[]}
          disabled={rate.disabled}
          required={rate.required}
        />
      )}
    </FieldWrapper>
  );
};

type CustomRateLabelFieldProps = {
  customRateLabel: ChargeFieldState;
};

export const CustomRateLabelField: React.FC<CustomRateLabelFieldProps> = ({
  customRateLabel,
}) => {
  return (
    <TextFormField
      {...{
        disabled: customRateLabel.disabled,
        required: customRateLabel.required,
        label: "Override label",
        helperText: "",
        formField: "customRateLabel",
      }}
    />
  );
};
